import Controller from '@ember/controller';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, get, set } from '@ember/object';
import { htmlSafe } from '@ember/template';
import config from 'garaje/config/environment';
import { formatSuccessRate } from 'garaje/components/health-dashboard/format-success-rate';

const TIME_RANGE_MS = {
  'Last 1 hour': 60 * 60 * 1000,
  'Last 24 hours': 24 * 60 * 60 * 1000,
  'Last 7 days': 7 * 24 * 60 * 60 * 1000,
  'Last 30 days': 30 * 24 * 60 * 60 * 1000,
};

// Bucket size for each time range (matches server-side chart bucket granularity).
// Used by liveUpdate() to fetch only the most recent bucket of timeline events.
const BUCKET_SIZE_MS = {
  'Last 1 hour': 5 * 60 * 1000, // 5-minute buckets
  'Last 24 hours': 60 * 60 * 1000, // 1-hour buckets
  'Last 7 days': 8 * 60 * 60 * 1000, // 8-hour buckets
  'Last 30 days': 24 * 60 * 60 * 1000, // 1-day buckets
};

function formatDuration(ms) {
  if (ms == null) return '–';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) {
    const s = (ms / 1000).toFixed(1).replace(/\.0$/, '');
    return `${s}s`;
  }
  if (ms < 3_600_000) {
    const m = Math.floor(ms / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatTimestampDate(epochMs) {
  return new Date(epochMs).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatTimestampTime(epochMs) {
  return new Date(epochMs).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function normalizeOutcome(outcome) {
  if (!outcome) return outcome;
  return outcome.replace(/^OPERATION_STATUS_/, '');
}

function formatOperationType(opType) {
  if (!opType) return null;
  return opType
    .replace(/^INTEGRATION_OPERATION_TYPE_/, '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDirection(direction) {
  if (!direction) return null;
  return direction
    .replace(/^OPERATION_DIRECTION_/, '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const CHART_LEFT_MARGIN = 30;

const CHART_COLORS = [
  '#7083ff', // gem.40
  '#ff8f33', // papaya.30
  '#4ec176', // cilantro.30
  '#b8be04', // pistachio.30
  '#fa4338', // red.40
  '#4141a2', // gem.70
  '#f76902', // papaya.40
  '#717684',
  '#018940',
  '#c25700',
  '#8e9d01',
  '#0ea5e9',
  '#e040fb',
  '#00bcd4',
  '#ff5252',
  '#69f0ae',
  '#ffeb3b',
  '#40c4ff',
  '#ff6d00',
  '#b388ff',
  '#f06292',
  '#80cbc4',
  '#aed581',
  '#ffcc02',
  '#4dd0e1',
  '#ff8a65',
  '#9575cd',
  '#a5d6a7',
  '#ef9a9a',
  '#80deea',
  '#ce93d8',
  '#ffab40',
  '#26a69a',
  '#ec407a',
  '#5c6bc0',
  '#d4e157',
  '#26c6da',
  '#ffa726',
  '#ab47bc',
  '#66bb6a',
];

// Builds a URL with query params, serializing arrays as repeated keys (e.g. installId=a&installId=b)
// so Spring Boot @RequestParam List<String> receives them correctly.
function buildUrl(base, params) {
  const qs = new URLSearchParams();
  for (const [key, val] of Object.entries(params)) {
    if (Array.isArray(val)) {
      for (const v of val) qs.append(key, v);
    } else if (val != null) {
      qs.append(key, String(val));
    }
  }
  return `${base}?${qs.toString()}`;
}

function mapTimelineEvent(raw) {
  return {
    eventId: raw.eventId,
    installId: raw.installId,
    processedAtMs: raw.envoyProcessedAt, // kept for chart bucketing
    processedDateDisplay: formatTimestampDate(raw.envoyProcessedAt),
    processedTimeDisplay: formatTimestampTime(raw.envoyProcessedAt),
    integrationType: raw.integrationType,
    integrationName: raw.integrationType, // enriched in _mappedEvents via installId lookup
    locationName: raw.locationName ?? (raw.locationId != null ? String(raw.locationId) : null),
    operationType: raw.operation?.type,
    operationTypeLabel: formatOperationType(raw.operation?.type),
    operationDefinition: raw.operation?.definition,
    operationStatus: normalizeOutcome(raw.outcome),
    failureType: raw.status ?? null,
    failureReason: raw.failureReason ?? null,
    displayReason: raw.displayReason ?? null,
    errorDetail: raw.errorDetail ?? null,
    recommendedAction: raw.recommendedAction ?? null,
    helpCenterUrl: raw.helpCenterUrl ?? null,
    direction: formatDirection(raw.direction),
    responsibleParty: raw.responsibleParty ?? null,
    primaryMetric: raw.primaryMetric ?? null,
    additionalMetrics: raw.additionalMetrics ?? null,
    endpoint: raw.endpoint ?? null,
    durationMillis: raw.durationMillis,
    durationDisplay: formatDuration(raw.durationMillis),
    correlationId: raw.correlationId ?? null,
  };
}

export default class IntegrationHealthDashboardController extends Controller {
  @tracked showLocationFilter = false;
  @tracked selectedTimeRange = 'Last 1 hour';
  @tracked selectedInstalls = []; // [] = All installs
  @tracked selectedEventTypes = []; // [] = All event types
  @tracked selectedLocations = []; // [] = All locations
  @tracked selectedIntegrationTypes = []; // [] = All integration types
  @tracked selectedStatuses = []; // [] = All statuses
  @tracked isLoading = false;
  @tracked page = 1;
  @tracked expandedEventId = null;
  @tracked _hoveredDotIndex = null;
  @tracked selectedEvent = null;
  @tracked relatedEvents = null; // null = not fetched / loading, [] = fetched but empty
  @tracked _relatedEventsLoading = false;
  @tracked isRelatedDrawerOpen = false;
  @tracked _hoveredEventTypeBarIndex = null;
  @tracked _hiddenEventTypes = [];
  @tracked _hiddenInstalls = [];
  @tracked _chartSvgPixelWidth = null;
  @tracked _eventTypeChartSvgPixelWidth = null;

  _relatedFetchGeneration = 0;

  @service ajaxFetch;
  @service state;
  @service currentAdmin;

  perPage = 20;
  _refreshTimer = null;

  @tracked _healthDataGeneral = null; // Contract 2a: GET /api/v1/monitoring/health — no time filter (used for dropdown filters)
  @tracked _healthDataCurrentRange = null; // Contract 2b: GET /api/v1/monitoring/health?startTime=…&endTime=… (selected window)
  @tracked _healthDataPreviousRange = null; // Contract 2c: same endpoint, window immediately before currentRange (comparison baseline for cards)
  @tracked _timelineData = null; // Contract 3: GET /api/v1/debugging/timeline

  timeRangeOptions = ['Last 1 hour', 'Last 24 hours', 'Last 7 days', 'Last 30 days'];

  // String equivalents for the filter drawer (single-select native <select> elements).
  // When multiple values are selected via the top bar, the drawer shows the first one.
  get selectedLocation() {
    return this.selectedLocations[0] ?? 'All locations';
  }
  get selectedInstall() {
    return this.selectedInstalls[0] ?? null;
  }
  get selectedEventType() {
    return this.selectedEventTypes[0] ?? 'All event types';
  }
  get selectedIntegrationType() {
    return this.selectedIntegrationTypes[0] ?? 'All integration types';
  }
  get selectedStatus() {
    return this.selectedStatuses[0] ?? 'All statuses';
  }

  get _mappedEvents() {
    const nameById = Object.fromEntries(this._integrations.map((i) => [String(i.installId), i.integrationName]));
    return (this._timelineData?.events ?? []).map((raw) => {
      const event = mapTimelineEvent(raw);
      event.integrationName = nameById[String(raw.installId)] ?? event.integrationType;
      return event;
    });
  }

  get _meta() {
    return this._timelineData?.meta ?? { total: 0, page: 1, perPage: this.perPage };
  }

  get _integrations() {
    return this._healthDataGeneral?.integrations ?? [];
  }

  get _filteredIntegrations() {
    const myLocationId = this.state.currentLocation?.id;
    return this._integrations.filter((i) => {
      if (this.selectedInstalls.length > 0) return this.selectedInstalls.includes(String(i.installId));
      if (!this.showLocationFilter) return i.location != null && String(i.location.id) === String(myLocationId);
      if (this.selectedLocations.length > 0) return this.selectedLocations.includes(String(i.location?.id));
      return true;
    });
  }

  // Base set of integrations for building dropdown options.
  // In regular mode: scoped to the current location.
  // In global overview mode: scoped to selectedLocations if any, otherwise all.
  get _scopedIntegrations() {
    const myLocationId = this.state.currentLocation?.id;
    if (!this.showLocationFilter) {
      return this._integrations.filter((i) => i.location != null && String(i.location.id) === String(myLocationId));
    }
    if (this.selectedLocations.length > 0) {
      return this._integrations.filter((i) => this.selectedLocations.includes(String(i.location?.id)));
    }
    return this._integrations;
  }

  get installOptions() {
    const installs = this._scopedIntegrations.map((i) => {
      const locLabel = i.location ? (i.location.name ?? String(i.location.id)) : null;
      const value = String(i.installId);
      return {
        label: locLabel ? `${i.integrationName} — ${locLabel}` : i.integrationName,
        value,
        integrationName: i.integrationName,
        locationId: i.location?.id ?? null,
        locationName: locLabel,
      };
    });
    return [{ label: 'All integrations', value: '' }, ...installs];
  }

  get locationOptions() {
    const seen = new Set();
    const locs = [];
    for (const i of this._integrations) {
      // Company-scoped integrations have no location — skip them here
      if (!i.location) continue;
      if (!seen.has(String(i.location.id))) {
        seen.add(String(i.location.id));
        locs.push({ label: i.location.name ?? String(i.location.id), value: String(i.location.id) });
      }
    }
    // If any selected location isn't in the health data (e.g. no integrations there yet,
    // or health hasn't loaded), still include it so the dropdown stays functional.
    for (const locId of this.selectedLocations) {
      if (!seen.has(locId)) {
        locs.push({ label: locId, value: locId });
        seen.add(locId);
      }
    }
    return [{ label: 'All locations', value: 'All locations' }, ...locs];
  }

  get eventTypeOptions() {
    const seen = new Set();
    const types = [];
    for (const i of this._scopedIntegrations) {
      for (const opType of i.activeOperationTypes ?? []) {
        if (!seen.has(opType)) {
          seen.add(opType);
          const stripped = opType.replace(/^INTEGRATION_OPERATION_TYPE_/, '');
          const label = stripped
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
          types.push({ label, value: opType });
        }
      }
    }
    return [{ label: 'All event types', value: 'All event types' }, ...types];
  }

  // Options without the "All" sentinel — used by PowerSelectMultiple in the top bar.
  get locationOptionsMulti() {
    return this.locationOptions.slice(1);
  }
  get installOptionsMulti() {
    return this.installOptions.slice(1);
  }
  get eventTypeOptionsMulti() {
    return this.eventTypeOptions.slice(1);
  }

  // Selected option arrays for PowerSelectMultiple @selected bindings.
  get selectedLocationOptions() {
    return this.locationOptions.filter((opt) => this.selectedLocations.includes(opt.value));
  }
  get selectedInstallOptions() {
    return this.installOptions.filter((opt) => this.selectedInstalls.includes(opt.value));
  }
  get selectedEventTypeOptions() {
    return this.eventTypeOptions.filter((opt) => this.selectedEventTypes.includes(opt.value));
  }

  // Single-option getters kept for any legacy template references.
  get selectedLocationOption() {
    return this.locationOptions.find((opt) => opt.value === this.selectedLocation) ?? null;
  }
  get selectedInstallOption() {
    const val = this.selectedInstall ?? '';
    return this.installOptions.find((opt) => opt.value === val) ?? null;
  }
  get selectedEventTypeOption() {
    return this.eventTypeOptions.find((opt) => opt.value === this.selectedEventType) ?? null;
  }

  get selectedIntegrationTypeOption() {
    return this.integrationTypeOptions.find((opt) => opt.value === this.selectedIntegrationType) ?? null;
  }

  get selectedStatusOption() {
    return this.statusOptions.find((opt) => opt.value === this.selectedStatus) ?? null;
  }

  get integrationTypeOptions() {
    const seen = new Set();
    const types = [];
    for (const i of this._scopedIntegrations) {
      if (i.integrationType && !seen.has(i.integrationType)) {
        seen.add(i.integrationType);
        const label = i.integrationType
          .replace(/^INTEGRATION_TYPE_/, '')
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        types.push({ label, value: i.integrationType });
      }
    }
    return [{ label: 'All integration types', value: 'All integration types' }, ...types];
  }

  statusOptions = [
    { label: 'All statuses', value: 'All statuses' },
    { label: 'Success', value: 'OPERATION_STATUS_OK' },
    { label: 'Failed', value: 'OPERATION_STATUS_FAILED' },
    { label: 'Partial', value: 'OPERATION_STATUS_PARTIAL' },
  ];

  get integrationTypeOptionsMulti() {
    return this.integrationTypeOptions.slice(1);
  }
  get statusOptionsMulti() {
    return this.statusOptions.slice(1);
  }
  get selectedIntegrationTypeOptions() {
    return this.integrationTypeOptions.filter((opt) => this.selectedIntegrationTypes.includes(opt.value));
  }
  get selectedStatusOptions() {
    return this.statusOptions.filter((opt) => this.selectedStatuses.includes(opt.value));
  }

  // Shared chart computation — accessed by individual chart getters below.
  // Returns a plain object only used internally; never passed directly to the template.
  get _chartComputed() {
    const points = this._timelineData?.chartData ?? [];
    if (!points.length) return null;

    const nonEmptyPoints = points.filter((p) => !p.isEmpty);
    if (!nonEmptyPoints.length) return null;

    // Build install color map from all install IDs seen across buckets, sorted by total count desc
    const installTotals = {};
    for (const p of points) {
      for (const [id, count] of Object.entries(p.installCounts ?? {})) {
        installTotals[id] = (installTotals[id] ?? 0) + count;
      }
    }
    const allInstallIds = Object.keys(installTotals).sort((a, b) => installTotals[b] - installTotals[a]);
    const installColorMap = {};
    allInstallIds.forEach((id, idx) => {
      installColorMap[id] = CHART_COLORS[idx % CHART_COLORS.length];
    });

    // Map installId → label using _integrations lookup
    const installOptions = this.installOptions; // [{label, value}, ...]
    const installLabelMap = {};
    for (const opt of installOptions) {
      if (opt.value) installLabelMap[opt.value] = opt.label;
    }

    const hidden = new Set(this._hiddenInstalls);
    const visibleInstallIds = allInstallIds.filter((id) => !hidden.has(id));

    const rates = nonEmptyPoints.map((p) => p.successRate);
    const minRate = Math.min(...rates);
    const maxRate = Math.max(...rates);
    const is1hRange = get(this, 'selectedTimeRange') === 'Last 1 hour';
    // For 1h zoom in tightly so small variations are visible; other ranges use coarser steps
    const buffer = is1hRange ? 2 : 5;
    const step = is1hRange ? 5 : 10;
    const yMin = Math.max(0, Math.floor((minRate - buffer) / step) * step);
    const yMax = Math.min(100, Math.ceil((maxRate + buffer) / step) * step);
    const yRange = yMax - yMin || 1;

    const yScale = (rate) => Math.round(90 - ((rate - yMin) / yRange) * 80);

    const singlePoint = points.length === 1;
    const slotW = (624 - CHART_LEFT_MARGIN) / points.length;
    const gap = Math.max(1, Math.round(slotW * 0.12));
    const bw = Math.max(1, Math.floor(slotW - gap));

    const pts = points.map((p, i) => {
      const barX = Math.round(i * slotW) + CHART_LEFT_MARGIN;
      const barTop = p.isEmpty ? 90 : yScale(p.successRate);
      const barHeight = p.isEmpty ? 0 : 90 - barTop;

      // Split bar into per-install colored segments proportional to install counts
      const installCounts = p.installCounts ?? {};
      const visibleTotal = visibleInstallIds.reduce((s, id) => s + (installCounts[id] ?? 0), 0);
      const r = 1;
      const rawSegs = [];
      let bottomY = 90;
      for (const id of visibleInstallIds) {
        const count = installCounts[id] ?? 0;
        if (!count || !barHeight) continue;
        const h = Math.round((count / Math.max(visibleTotal, 1)) * barHeight);
        if (!h) continue;
        const y = bottomY - h;
        rawSegs.push({ installId: id, count, y, height: h, color: installColorMap[id] });
        bottomY -= h;
      }
      const segments = rawSegs.map((seg, si) => {
        const isTop = si === rawSegs.length - 1;
        const x = barX;
        const w = bw;
        const { y, height: h } = seg;
        const pathD = isTop
          ? `M${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} L${x},${
              y + h
            } L${x},${y + r} Q${x},${y} ${x + r},${y} Z`
          : `M${x},${y} L${x + w},${y} L${x + w},${y + h} L${x},${y + h} Z`;
        return { ...seg, pathD };
      });

      return {
        x: Math.round(i * slotW + slotW / 2) + CHART_LEFT_MARGIN,
        barX,
        barWidth: bw,
        y: p.isEmpty ? null : barTop,
        barHeight,
        startMs: p.startMs,
        successRate: p.isEmpty ? null : p.successRate,
        isEmpty: p.isEmpty ?? false,
        segments,
        installCounts,
      };
    });

    // Always render 7 evenly-distributed labels: first bucket, 5 interior, last bucket ("Now").
    // Bucket counts from the backend: 1h=12x5m | 24h=24x1h | 7d=21x8h | 30d=30x1d
    const is1h = get(this, 'selectedTimeRange') === 'Last 1 hour';
    const is24h = get(this, 'selectedTimeRange') === 'Last 24 hours';
    let xLabels;
    if (singlePoint) {
      xLabels = [{ x: Math.round((624 + CHART_LEFT_MARGIN) / 2), label: 'Now', textAnchor: 'middle' }];
    } else {
      const targetLabelCount = 7;
      const labelIndices = Array.from({ length: targetLabelCount }, (_, i) =>
        Math.round((i * (pts.length - 1)) / (targetLabelCount - 1)),
      );
      const lastIdx = pts.length - 1;
      xLabels = labelIndices.map((i, li) => ({
        x: pts[i].x,
        label:
          i === lastIdx
            ? 'Now'
            : is1h || is24h
              ? new Date(pts[i].startMs).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
              : new Date(pts[i].startMs).toLocaleString('en-US', { month: 'short', day: 'numeric' }),
        textAnchor: li === 0 ? 'start' : li === labelIndices.length - 1 ? 'end' : 'middle',
      }));
    }

    const mid = Math.round((yMax + yMin) / 2);
    const gridLines = [yMax, mid, yMin].map((rate) => ({
      y: yScale(rate),
      labelY: yScale(rate) + 4,
      label: `${rate}%`,
      labelX: CHART_LEFT_MARGIN - 2,
    }));

    const legend = allInstallIds.map((id) => ({
      installId: id,
      label: installLabelMap[id] ?? id,
      color: installColorMap[id],
      isHidden: hidden.has(id),
    }));

    return { bars: pts, xLabels, gridLines, legend };
  }

  // Individual chart getters expose only primitives/arrays to the template,
  // avoiding Ember's "dynamic component definition" error when returning POJOs.
  get hasChartData() {
    return this._chartComputed !== null;
  }
  get chartBars() {
    return this._chartComputed?.bars ?? [];
  }
  get chartHoveredBarIndex() {
    return this._hoveredDotIndex;
  }
  get chartGridLines() {
    return this._chartComputed?.gridLines ?? [];
  }
  get chartXLabels() {
    return this._chartComputed?.xLabels ?? [];
  }
  get chartInstallLegend() {
    return this._chartComputed?.legend ?? [];
  }

  get chartHoveredPoint() {
    if (this._hoveredDotIndex === null) return null;
    const bars = this._chartComputed?.bars ?? [];
    const bar = bars[this._hoveredDotIndex];
    if (!bar || bar.isEmpty) return null;

    const d = new Date(bar.startMs);
    const is1h = get(this, 'selectedTimeRange') === 'Last 1 hour';
    const is24h = get(this, 'selectedTimeRange') === 'Last 24 hours';
    const dateValue =
      is1h || is24h
        ? d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    const legend = this._chartComputed?.legend ?? [];
    const installCounts = bar.installCounts ?? {};
    const installRows = legend.filter((item) => !item.isHidden && (installCounts[item.installId] ?? 0) > 0);

    const pixelWidth = this._chartSvgPixelWidth;
    const scaleFactor = pixelWidth ? pixelWidth / 624 : 1;
    const screenX = bar.x * scaleFactor;
    const screenY = bar.y * scaleFactor;
    const tooltipStyle = htmlSafe(
      `left: ${screenX}px; top: ${screenY}px; transform: translateX(-50%) translateY(calc(-100% - 6px));`,
    );

    return {
      x: bar.x,
      tooltipStyle,
      dateValue,
      successRate: bar.successRate != null ? formatSuccessRate(bar.successRate) : bar.successRate,
      installRows: installRows.map((item) => ({
        dotStyle: htmlSafe(`background: ${item.color}`),
        label: item.label,
        count: installCounts[item.installId] ?? 0,
      })),
    };
  }

  @action onChartMouseMove(event) {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    this._chartSvgPixelWidth = rect.width;
    const mouseX = ((event.clientX - rect.left) / rect.width) * 624;
    const bars = this._chartComputed?.bars ?? [];
    if (!bars.length) return;
    let closest = -1;
    let minDist = Infinity;
    for (let i = 0; i < bars.length; i++) {
      if (bars[i].isEmpty) continue;
      const dist = Math.abs(bars[i].x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    this._hoveredDotIndex = closest === -1 ? null : closest;
  }

  @action onChartLeave() {
    this._hoveredDotIndex = null;
  }

  get _eventTypeChartComputed() {
    const points = this._timelineData?.chartData ?? [];
    if (!points.length) return null;

    const typeTotals = {};
    for (const p of points) {
      for (const [k, v] of Object.entries(p.typeCounts ?? {})) {
        typeTotals[k] = (typeTotals[k] ?? 0) + v;
      }
    }
    const allTypes = Object.keys(typeTotals).sort((a, b) => typeTotals[b] - typeTotals[a]);
    if (!allTypes.length) return null;

    const hidden = new Set(this._hiddenEventTypes);
    const visibleTypes = allTypes.filter((t) => !hidden.has(t));

    const colorMap = {};
    allTypes.forEach((t, idx) => {
      colorMap[t] = CHART_COLORS[idx % CHART_COLORS.length];
    });

    const bucketVisibleTotals = points.map((p) => visibleTypes.reduce((s, t) => s + (p.typeCounts?.[t] ?? 0), 0));
    const maxTotal = Math.max(...bucketVisibleTotals, 1);

    const chartBottom = 90;
    const chartH = 80;
    const yScale = (count) => Math.round((count / maxTotal) * chartH);

    const slotW = (624 - CHART_LEFT_MARGIN) / points.length;
    const gap = Math.max(1, Math.round(slotW * 0.12));
    const bw = Math.max(1, Math.floor(slotW - gap));

    const bars = points.map((p, i) => {
      const counts = p.typeCounts ?? {};
      const total = bucketVisibleTotals[i];
      const barX = Math.round(i * slotW) + CHART_LEFT_MARGIN;
      let bottomY = chartBottom;
      const rawSegs = [];
      for (const type of visibleTypes) {
        const count = counts[type] ?? 0;
        if (!count) continue;
        const h = yScale(count);
        if (!h) continue;
        const y = bottomY - h;
        rawSegs.push({ type, count, y, height: h, color: colorMap[type] });
        bottomY -= h;
      }
      const r = 1;
      const segments = rawSegs.map((seg, si) => {
        const isTop = si === rawSegs.length - 1;
        const x = barX;
        const w = bw;
        const { y, height: h } = seg;
        const pathD = isTop
          ? `M${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} L${x},${
              y + h
            } L${x},${y + r} Q${x},${y} ${x + r},${y} Z`
          : `M${x},${y} L${x + w},${y} L${x + w},${y + h} L${x},${y + h} Z`;
        return { ...seg, isTop, pathD };
      });
      return {
        x: Math.round(i * slotW + slotW / 2) + CHART_LEFT_MARGIN,
        barX,
        barWidth: bw,
        total,
        topY: total > 0 ? chartBottom - yScale(total) : chartBottom,
        segments,
        startMs: p.startMs,
        isEmpty: total === 0,
      };
    });

    const gridLines = [maxTotal, Math.round(maxTotal / 2), 0].map((count) => ({
      y: chartBottom - yScale(count),
      labelY: chartBottom - yScale(count) + 4,
      label: count >= 1000 ? `${(count / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(count),
      labelX: CHART_LEFT_MARGIN - 2,
    }));

    const legend = allTypes.map((type) => ({
      type,
      label: type
        .replace(/^INTEGRATION_OPERATION_TYPE_/i, '')
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      color: colorMap[type],
      isHidden: hidden.has(type),
    }));

    return { bars, gridLines, legend };
  }

  get hasEventTypeChartData() {
    return this._eventTypeChartComputed !== null;
  }
  get eventTypeChartBars() {
    return this._eventTypeChartComputed?.bars ?? [];
  }
  get eventTypeChartGridLines() {
    return this._eventTypeChartComputed?.gridLines ?? [];
  }
  get eventTypeChartLegend() {
    return this._eventTypeChartComputed?.legend ?? [];
  }
  get eventTypeChartHoveredBarIndex() {
    return this._hoveredEventTypeBarIndex;
  }

  get eventTypeChartHoveredPoint() {
    if (this._hoveredEventTypeBarIndex === null) return null;
    const bars = this._eventTypeChartComputed?.bars ?? [];
    const bar = bars[this._hoveredEventTypeBarIndex];
    if (!bar || bar.isEmpty) return null;

    const d = new Date(bar.startMs);
    const is1h = get(this, 'selectedTimeRange') === 'Last 1 hour';
    const is24h = get(this, 'selectedTimeRange') === 'Last 24 hours';
    const dateValue =
      is1h || is24h
        ? d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    const pixelWidth = this._eventTypeChartSvgPixelWidth;
    const scaleFactor = pixelWidth ? pixelWidth / 624 : 1;
    const screenX = bar.x * scaleFactor;
    const screenY = bar.topY * scaleFactor;
    const tooltipStyle = htmlSafe(
      `left: ${screenX}px; top: ${screenY}px; transform: translateX(-50%) translateY(calc(-100% - 6px));`,
    );

    const typeRows = bar.segments.map((seg) => ({
      label: seg.type
        .replace(/^INTEGRATION_OPERATION_TYPE_/i, '')
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      count: seg.count,
      dotStyle: htmlSafe(`background: ${seg.color}`),
    }));

    return {
      x: bar.x,
      tooltipStyle,
      dateValue,
      typeRows,
    };
  }

  @action toggleEventTypeSeries(type) {
    if (this._hiddenEventTypes.includes(type)) {
      this._hiddenEventTypes = this._hiddenEventTypes.filter((t) => t !== type);
    } else {
      this._hiddenEventTypes = [...this._hiddenEventTypes, type];
    }
  }

  @action toggleInstallSeries(installId) {
    if (this._hiddenInstalls.includes(installId)) {
      this._hiddenInstalls = this._hiddenInstalls.filter((id) => id !== installId);
    } else {
      this._hiddenInstalls = [...this._hiddenInstalls, installId];
    }
  }

  @action onEventTypeChartMouseMove(event) {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    this._eventTypeChartSvgPixelWidth = rect.width;
    const mouseX = ((event.clientX - rect.left) / rect.width) * 624;
    const bars = this._eventTypeChartComputed?.bars ?? [];
    if (!bars.length) return;
    let closest = -1;
    let minDist = Infinity;
    for (let i = 0; i < bars.length; i++) {
      if (bars[i].isEmpty) continue;
      const dist = Math.abs(bars[i].x - mouseX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    this._hoveredEventTypeBarIndex = closest === -1 ? null : closest;
  }

  @action onEventTypeChartLeave() {
    this._hoveredEventTypeBarIndex = null;
  }

  get filteredEvents() {
    return this._mappedEvents;
  }

  get recordCount() {
    return this._meta.total;
  }

  get pageCount() {
    return Math.ceil(this.recordCount / this.perPage);
  }

  get paginatedEvents() {
    return this._mappedEvents;
  }

  startAutoRefresh() {
    this.stopAutoRefresh();
    this._refreshTimer = setInterval(() => this.liveUpdate(), 60_000);
  }

  stopAutoRefresh() {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.stopAutoRefresh();
  }

  async fetchData() {
    this.isLoading = true;
    const now = Date.now();
    const rangeMs = TIME_RANGE_MS[get(this, 'selectedTimeRange')];
    const startTime = now - rangeMs;
    const previousRangeEnd = startTime;
    const previousRangeStart = startTime - rangeMs;

    const companyId = String(this.state.currentCompany?.id);
    const isGlobal = this.showLocationFilter;
    const locationId = this.state.currentLocation?.id;

    const activeFilters = {
      ...(this.selectedInstalls.length > 0 && { installId: this.selectedInstalls }),
      ...(this.selectedEventTypes.length > 0 && { eventType: this.selectedEventTypes }),
      ...(this.selectedLocations.length > 0 && { location: this.selectedLocations }),
      ...(this.selectedStatuses.length > 0 && { outcome: this.selectedStatuses }),
      ...(this.selectedIntegrationTypes.length > 0 && { integrationType: this.selectedIntegrationTypes }),
    };

    const healthBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/monitoring/health`;
    const timelineBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/debugging/timeline`;

    try {
      const [healthDataGeneral, healthDataCurrentRange, healthDataPreviousRange, timelineData] = await Promise.all([
        this.ajaxFetch.request(
          buildUrl(healthBase, {
            companyId,
            rawIntegrationOperations: 'true',
            ...(!isGlobal && locationId && { locationId: String(locationId) }),
          }),
        ),
        this.ajaxFetch.request(
          buildUrl(healthBase, {
            companyId,
            rawIntegrationOperations: 'true',
            startTime: String(startTime),
            endTime: String(now),
            ...activeFilters,
            ...(!isGlobal && locationId && { locationId: String(locationId) }),
          }),
        ),
        this.ajaxFetch.request(
          buildUrl(healthBase, {
            companyId,
            rawIntegrationOperations: 'true',
            startTime: String(previousRangeStart),
            endTime: String(previousRangeEnd),
            ...activeFilters,
            ...(!isGlobal && locationId && { locationId: String(locationId) }),
          }),
        ),
        this.ajaxFetch.request(
          buildUrl(timelineBase, {
            companyId,
            startTime: String(startTime),
            endTime: String(now),
            page: String(this.page),
            perPage: String(this.perPage),
            rawIntegrationOperations: 'true',
            ...activeFilters,
          }),
        ),
      ]);
      this._healthDataGeneral = healthDataGeneral;
      this._healthDataCurrentRange = healthDataCurrentRange;
      this._healthDataPreviousRange = healthDataPreviousRange;
      this._timelineData = timelineData;
    } finally {
      this.isLoading = false;
    }
  }

  @action lockScroll() {
    const hasScrollBar = window.innerWidth > document.body.clientWidth;
    if (hasScrollBar) {
      document.body.style.marginRight = `${window.innerWidth - document.body.clientWidth}px`;
    }
    document.body.classList.add('overflow-hidden');
  }

  @action unlockScroll() {
    document.body.classList.remove('overflow-hidden');
    document.body.style.marginRight = '';
  }

  // Silent incremental refresh — no loading overlay. Fetches health stats for the full
  // selected range plus timeline events for the most recent bucket only, then merges new
  // events into the existing list without duplicates. Called by the 60-second auto-refresh
  // timer so the page stays live without the pulsing overlay.
  async liveUpdate() {
    const now = Date.now();
    const rangeMs = TIME_RANGE_MS[get(this, 'selectedTimeRange')];
    const bucketMs = BUCKET_SIZE_MS[get(this, 'selectedTimeRange')];
    const startTime = now - rangeMs;
    const previousRangeEnd = startTime;
    const previousRangeStart = startTime - rangeMs;
    const bucketStart = now - bucketMs;

    const companyId = String(this.state.currentCompany?.id);
    const isGlobal = this.showLocationFilter;
    const locationId = this.state.currentLocation?.id;

    const activeFilters = {
      ...(this.selectedInstalls.length > 0 && { installId: this.selectedInstalls }),
      ...(this.selectedEventTypes.length > 0 && { eventType: this.selectedEventTypes }),
      ...(this.selectedLocations.length > 0 && { location: this.selectedLocations }),
      ...(this.selectedStatuses.length > 0 && { outcome: this.selectedStatuses }),
      ...(this.selectedIntegrationTypes.length > 0 && { integrationType: this.selectedIntegrationTypes }),
    };

    const healthBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/monitoring/health`;
    const timelineBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/debugging/timeline`;

    try {
      const [healthDataCurrentRange, healthDataPreviousRange, newTimelineData] = await Promise.all([
        this.ajaxFetch.request(
          buildUrl(healthBase, {
            companyId,
            rawIntegrationOperations: 'true',
            startTime: String(startTime),
            endTime: String(now),
            ...activeFilters,
            ...(!isGlobal && locationId && { locationId: String(locationId) }),
          }),
        ),
        this.ajaxFetch.request(
          buildUrl(healthBase, {
            companyId,
            rawIntegrationOperations: 'true',
            startTime: String(previousRangeStart),
            endTime: String(previousRangeEnd),
            ...activeFilters,
            ...(!isGlobal && locationId && { locationId: String(locationId) }),
          }),
        ),
        this.ajaxFetch.request(
          buildUrl(timelineBase, {
            companyId,
            startTime: String(bucketStart),
            endTime: String(now),
            page: '1',
            perPage: String(this.perPage),
            sinceTime: String(bucketStart),
            rawIntegrationOperations: 'true',
            ...activeFilters,
          }),
        ),
      ]);

      this._healthDataCurrentRange = healthDataCurrentRange;
      this._healthDataPreviousRange = healthDataPreviousRange;

      // Merge new events into the existing list, prepending unseen ones.
      const incoming = newTimelineData?.events ?? [];
      const existingIds = new Set((this._timelineData?.events ?? []).map((e) => e.eventId));
      const fresh = incoming.filter((e) => !existingIds.has(e.eventId));

      // Slide the chart forward using the bucket(s) returned for the last-bucket window.
      // For each returned bucket: update it in-place if we already have that startMs,
      // otherwise append it and drop the oldest bucket to keep the array length stable.
      const newChartBuckets = newTimelineData?.chartData ?? [];
      const existingChart = this._timelineData?.chartData ?? [];
      let updatedChart = existingChart;
      if (newChartBuckets.length > 0 && existingChart.length > 0) {
        updatedChart = [...existingChart];
        for (const bucket of newChartBuckets) {
          const idx = updatedChart.findIndex((b) => b.startMs === bucket.startMs);
          if (idx >= 0) {
            updatedChart[idx] = bucket; // refresh in-place (same slot, more events arrived)
          } else {
            updatedChart = [...updatedChart.slice(1), bucket]; // slide window forward
          }
        }
      }

      if (fresh.length > 0 || updatedChart !== existingChart) {
        const mergedEvents =
          fresh.length > 0
            ? [...fresh, ...(this._timelineData?.events ?? [])].slice(0, this.perPage)
            : this._timelineData?.events;
        this._timelineData = {
          ...this._timelineData,
          chartData: updatedChart,
          events: mergedEvents,
        };
      }
    } catch {
      // silently fail — same behaviour as fetchData
    }
  }

  @action setTimeRange(value) {
    set(this, 'selectedTimeRange', value);
    this.page = 1;
    this.fetchData();
  }

  // Top-bar multi-select actions (receive array of option objects from PowerSelectMultiple).
  @action setLocations(opts) {
    this.selectedLocations = opts.map((o) => o?.value ?? o);
    this.selectedInstalls = [];
    this.page = 1;
    this.fetchData();
  }

  @action setInstalls(opts) {
    const values = opts.map((o) => o?.value ?? o).filter(Boolean);
    this.selectedInstalls = values;
    // Mirror single-install auto-selection: when exactly one install is chosen,
    // auto-select its location and integration type (same as the old setInstall action).
    if (values.length === 1) {
      const installData = this._integrations.find((i) => String(i.installId) === values[0]);
      this.selectedLocations = installData?.location ? [String(installData.location.id)] : [];
      this.selectedIntegrationTypes = installData?.integrationType ? [installData.integrationType] : [];
    } else {
      this.selectedLocations = [];
      this.selectedIntegrationTypes = [];
    }
    this.page = 1;
    this.fetchData();
  }

  @action setEventTypes(opts) {
    this.selectedEventTypes = opts.map((o) => o?.value ?? o);
    this.page = 1;
    this.fetchData();
  }

  @action setPage(value) {
    this.page = value;
    if (value === 1) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
    this.fetchData();
  }

  @action toggleExpand(eventId) {
    this.expandedEventId = this.expandedEventId === eventId ? null : eventId;
  }

  @action openDrawer(event) {
    this.selectedEvent = event;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
    if (event.correlationId) {
      this._relatedEventsLoading = true;
      this.fetchRelatedEvents(event.correlationId);
    }
  }

  @action closeDrawer() {
    this.selectedEvent = null;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
  }

  async fetchRelatedEvents(correlationId) {
    const gen = ++this._relatedFetchGeneration;
    this._relatedEventsLoading = true;
    const companyId = String(this.state.currentCompany?.id);
    const endTime = Date.now();
    const startTime = endTime - 30 * 24 * 60 * 60 * 1000; // last 30 days
    try {
      const data = await this.ajaxFetch.request(
        buildUrl(`${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v1/debugging/timeline`, {
          companyId,
          correlationId,
          startTime: String(startTime),
          endTime: String(endTime),
          perPage: '50',
        }),
      );
      if (gen !== this._relatedFetchGeneration) return;
      const nameById = Object.fromEntries(this._integrations.map((i) => [String(i.installId), i.integrationName]));
      const currentEventId = this.selectedEvent?.eventId;
      this.relatedEvents = (data?.events ?? [])
        .map((raw) => {
          const event = mapTimelineEvent(raw);
          event.integrationName = nameById[String(raw.installId)] ?? event.integrationType;
          return event;
        })
        .filter((e) => e.eventId !== currentEventId);
    } catch {
      if (gen !== this._relatedFetchGeneration) return;
      this.relatedEvents = [];
    } finally {
      if (gen === this._relatedFetchGeneration) {
        this._relatedEventsLoading = false;
      }
    }
  }

  @action openRelatedDrawer() {
    this.isRelatedDrawerOpen = true;
  }

  @action closeRelatedDrawer() {
    this.isRelatedDrawerOpen = false;
  }

  @action openRelatedEventDetail(event) {
    this.selectedEvent = event;
    this.isRelatedDrawerOpen = false;
    this.relatedEvents = null;
    if (event.correlationId) {
      this._relatedEventsLoading = true;
      this.fetchRelatedEvents(event.correlationId);
    }
  }

  @action closeActiveDrawer() {
    this.selectedEvent = null;
    this.relatedEvents = null;
    this.isRelatedDrawerOpen = false;
  }

  @action setIntegrationTypes(opts) {
    this.selectedIntegrationTypes = opts.map((o) => o?.value ?? o);
    this.page = 1;
    this.fetchData();
  }

  @action setStatuses(opts) {
    this.selectedStatuses = opts.map((o) => o?.value ?? o);
    this.page = 1;
    this.fetchData();
  }
}
